import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { format, startOfDay, endOfDay } from 'date-fns';
import {
    Autocomplete,
    DatepickerSinglePicker,
    DatepickerTextField,
    OnDateChangeProps,
} from '@avangard/ui/core';

import { usePageFilters, PageFiltersDrawer } from '@modules/shared/organisms';
import { useGeoFilters } from '@modules/shared/hooks';
import { useLaps } from '@modules/lap/hooks';

import type { LocationEntity, RegionEntity } from '@modules/shared/entities';
import type { LapEntity } from '@modules/lap/entities';
import type { SummaryDataFilters } from '@modules/summary-results/hooks';

const DrawerBody = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    padding: 24px;
    overflow: auto;

    ${p => p.theme.breakpoints.down('sm')} {
        padding: 12px;
    }
`;

const FilterColumn = styled.div`
    display: flex;
    flex-wrap: wrap;

    > * {
        &:not(:last-child) {
            margin-bottom: 24px;
        }
    }
`;

const FilterDatesRow = styled.div`
    display: flex;
    flex-wrap: wrap;

    > * {
        width: calc(50% - 10px);

        &:first-child {
            position: relative;
            margin-inline-end: 20px;
        }
    }
`;

const DrawerFilters = (): React.ReactElement => {
    const { t } = useTranslation(['common']);

    const {
        filters,
        setFilterValue,
        handleChangeAutocomplete,

        getOptionValue,
        getMultipleOptionValue,
        getOptionLabel,
        getOptionSelected,
    } = usePageFilters<SummaryDataFilters>();

    const { laps, loading: lapsLoading } = useLaps();

    const { regions, regionsLoading, locations, locationsCount, locationsLoading } = useGeoFilters({
        refRegionId: filters.regionId,
    });

    const handleChangeDate = (field: string) => (data: OnDateChangeProps) => {
        if (!data.date) {
            return;
        }

        const date = field === 'from' ? startOfDay(data.date) : endOfDay(data.date);

        setFilterValue(field, format(date, 'yyyy-MM-dd'));
    };

    return (
        <PageFiltersDrawer>
            {() => (
                <DrawerBody>
                    <FilterColumn>
                        <Autocomplete<LapEntity | null, true, false, false>
                            multiple
                            fullWidth
                            id='lapIds'
                            label={t('common:forms.labels.lap_dates') ?? ''}
                            placeholder={t('common:forms.placeholder.choose_lap_dates') ?? ''}
                            options={laps}
                            value={getMultipleOptionValue(laps, filters.lapIds, 'id')}
                            disabled={lapsLoading}
                            getOptionLabel={getOptionLabel('name')}
                            getOptionSelected={getOptionSelected()}
                            onChange={handleChangeAutocomplete({
                                multiple: true,
                                extractMultiplyBy: 'id',
                            })}
                        />

                        <FilterDatesRow>
                            <DatepickerSinglePicker
                                date={filters.from ? new Date(filters.from) : null}
                                onDateChange={handleChangeDate('from')}
                            >
                                {datepickerProps => {
                                    const { inputProps, inputComponent } = datepickerProps;

                                    return (
                                        <DatepickerTextField
                                            id='from'
                                            label={t('forms.labels.date_from')}
                                            placeholder={t('common:forms.labels.date') ?? ''}
                                            InputProps={{ inputComponent }}
                                            {...inputProps}
                                        />
                                    );
                                }}
                            </DatepickerSinglePicker>

                            <DatepickerSinglePicker
                                date={filters.to ? new Date(filters.to) : null}
                                onDateChange={handleChangeDate('to')}
                            >
                                {datepickerProps => {
                                    const { inputProps, inputComponent } = datepickerProps;

                                    return (
                                        <DatepickerTextField
                                            id='to'
                                            label={t('forms.labels.date_to')}
                                            placeholder={t('common:forms.labels.date') ?? ''}
                                            InputProps={{ inputComponent }}
                                            {...inputProps}
                                        />
                                    );
                                }}
                            </DatepickerSinglePicker>
                        </FilterDatesRow>

                        <Autocomplete<RegionEntity | null, false, false, false>
                            fullWidth
                            id='regionId'
                            label={t('common:forms.labels.region') ?? ''}
                            placeholder={t('common:forms.placeholder.choose_region') ?? ''}
                            options={regions}
                            value={getOptionValue(regions, filters.regionId)}
                            disabled={regionsLoading}
                            getOptionLabel={getOptionLabel('name')}
                            getOptionSelected={getOptionSelected()}
                            onChange={handleChangeAutocomplete({ reset: ['locationId'] })}
                        />

                        <Autocomplete<LocationEntity | null, false, false, false>
                            fullWidth
                            id='locationId'
                            label={t('common:forms.labels.location') ?? ''}
                            placeholder={t('common:forms.placeholder.choose_location') ?? ''}
                            options={locations}
                            value={getOptionValue(locations, filters.locationId)}
                            disabled={!filters.regionId || locationsCount === 0 || locationsLoading}
                            getOptionLabel={getOptionLabel('name')}
                            getOptionSelected={getOptionSelected()}
                            onChange={handleChangeAutocomplete()}
                        />
                    </FilterColumn>
                </DrawerBody>
            )}
        </PageFiltersDrawer>
    );
};

export { DrawerFilters };
